// Admin API client for the api.podryvanov.ru "Tunnels" panel. Single operator,
// password login. Access token in memory; refresh cookie httpOnly.

export interface Tunnel {
  id: string
  label: string
  comment: string | null
  expiryDays: number
  deviceLimit: number
  trafficLimitGb: number
  expiresAt: string | null
  status: 'provisioning' | 'active' | 'error' | 'revoked'
  vless: { ready: boolean; link: string | null }
  wg: { ready: boolean }
  subscription: { url: string | null }
  usage: { uploadBytes: number; downloadBytes: number; usedGb: number; limitGb: number }
  download: { url: string | null; live: boolean; used: boolean; usedAt: string | null; expiresAt: string | null }
  ip: string
  createdAt: string
  updatedAt: string
}

// An already-issued VPN config (from svoisozvon), surfaced for inventory.
export interface VpnConfig {
  id: string
  name: string | null
  username: string | null
  legacyUid: string | null
  clientIP: string
  profileId: string
  status: 'active' | 'revoked'
  issuedAt: string | null
  comment: string | null
  canDownload: boolean
  linkedUser: boolean
}

// Availability of a platform through the egress IP that routes it. Filled by
// the checker on the VPN box; read-only here.
export interface SiteStatus {
  key: string
  label: string
  egressIp: string
  url: string
  state: 'ok' | 'blocked' | 'unreachable' | 'error' | 'unknown'
  ok: boolean
  httpCode: number | null
  latencyMs: number | null
  checkedAt: string
}

// Master egress switch — where ALL traffic exits. Set by admin, applied by the
// box poller within ~1 min (appliedMode confirms it's live).
export interface EgressOption { mode: string; label: string; desc: string }
export interface EgressState {
  mode: string
  appliedMode: string | null
  appliedAt: string | null
  applied: boolean
  updatedAt: string
  modes: EgressOption[]
}

// ── статистика панели (обзор / клиенты / сайты) ──
export interface Overview {
  clientsTotal: number
  active: number
  onlineNow: number
  expiringSoon: number
  trafficUp: number
  trafficDown: number
  servers: { de: { up: boolean }; nl: { up: boolean }; failoverMode: string }
  generatedAt: number
}
export interface ClientStat {
  id: string
  name: string
  label: string
  tunnelId: string | null
  hasTunnel: boolean
  status: string
  expiresAt: string | null
  trafficLimitGb: number
  online: boolean
  devices: number
  ips: string[]
  up: number
  down: number
  total: number
  lastOnline: number
  inbounds: string[]
}
export interface DomainHit { domain: string; hits: number }

let accessToken: string | null = null
export function setToken(t: string | null) { accessToken = t }
export function getToken() { return accessToken }

class ApiError extends Error {
  status: number
  constructor(status: number, message: string) { super(message); this.status = status }
}

async function refresh(): Promise<boolean> {
  try {
    const res = await fetch('/api/portal/admin/refresh', { method: 'POST', credentials: 'include' })
    if (!res.ok) return false
    const data = await res.json()
    accessToken = data.accessToken
    return true
  } catch { return false }
}

async function raw(path: string, init: RequestInit = {}, retry = true): Promise<Response> {
  const headers = new Headers(init.headers)
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
  const res = await fetch(path, { ...init, headers, credentials: 'include' })
  if (res.status === 401 && retry && accessToken) {
    if (await refresh()) return raw(path, init, false)
  }
  return res
}

async function json<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  const res = await raw(path, { ...init, headers })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new ApiError(res.status, (data as any).error || `Error ${res.status}`)
  return data as T
}

export const api = {
  // ── auth ──
  login: (password: string) =>
    json<{ ok: true; accessToken: string }>('/api/portal/admin/login', { method: 'POST', body: JSON.stringify({ password }) }),
  refresh,
  logout: () => json('/api/portal/admin/logout', { method: 'POST' }),
  me: () => json<{ ok: true; admin: true }>('/api/portal/admin/me'),

  // ── tunnels ──
  listIps: () => json<{ ips: string[]; default: string }>('/api/portal/admin/ips'),
  listTunnels: () => json<{ tunnels: Tunnel[] }>('/api/portal/admin/tunnels'),
  createTunnel: (label: string, comment: string, ip: string, expiryDays?: number, deviceLimit?: number, trafficLimitGb?: number) =>
    json<{ tunnel: Tunnel }>('/api/portal/admin/tunnels', { method: 'POST', body: JSON.stringify({ label, comment, ip, expiryDays, deviceLimit, trafficLimitGb }) }),
  updateTunnel: (id: string, patch: { label?: string; comment?: string }) =>
    json<{ tunnel: Tunnel }>(`/api/portal/admin/tunnels/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  revokeTunnel: (id: string) => json<{ tunnel: Tunnel }>(`/api/portal/admin/tunnels/${id}/revoke`, { method: 'POST' }),
  deleteTunnel: (id: string) => json(`/api/portal/admin/tunnels/${id}`, { method: 'DELETE' }),
  makeDownloadLink: (id: string) =>
    json<{ tunnel: Tunnel }>(`/api/portal/admin/tunnels/${id}/dl-link`, { method: 'POST' }),
  makeSubLink: (id: string) =>
    json<{ tunnel: Tunnel }>(`/api/portal/admin/tunnels/${id}/sub-link`, { method: 'POST' }),

  // ── ранее выданные конфиги (inventory) ──
  listConfigs: (all = false) => json<{ configs: VpnConfig[] }>(`/api/portal/admin/configs${all ? '?all=1' : ''}`),
  setConfigComment: (id: string, comment: string) =>
    json<{ config: VpnConfig }>(`/api/portal/admin/configs/${id}`, { method: 'PATCH', body: JSON.stringify({ comment }) }),
  revokeConfig: (id: string) => json(`/api/portal/admin/configs/${id}/revoke`, { method: 'POST' }),
  syncConfigs: () => json('/api/portal/admin/sync', { method: 'POST' }),

  // ── статистика панели ──
  overview: () => json<Overview>('/api/portal/admin/overview'),
  clientsStats: () => json<ClientStat[]>('/api/portal/admin/clients-stats'),
  domains: () => json<{ overall: DomainHit[]; generatedAt: number }>('/api/portal/admin/domains'),
  clientDomains: (id: string) => json<DomainHit[]>(`/api/portal/admin/clients/${encodeURIComponent(id)}/domains`),

  // ── статус площадок ──
  siteStatus: () =>
    json<{ sites: SiteStatus[]; lastCheckedAt: string | null; stale: boolean }>('/api/portal/admin/site-status'),

  // ── egress-режим (рубильник выходного IP) ──
  egressMode: () => json<EgressState>('/api/portal/admin/egress-mode'),
  setEgressMode: (mode: string) =>
    json<EgressState>('/api/portal/admin/egress-mode', { method: 'POST', body: JSON.stringify({ mode }) }),
  // Returns the .conf text for a blob download.
  configText: async (id: string): Promise<string> => {
    const res = await raw(`/api/portal/admin/configs/${id}/config`)
    if (!res.ok) throw new ApiError(res.status, 'Не удалось скачать.')
    return res.text()
  },
}

export { ApiError }
